import {
  Box,
  Button,
  Checkbox,
  FormField,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import { useCallback, useEffect, useMemo, useState } from "react";

import { EnumeratedIconikAsset } from "@/components/iconik/types";
import { useNotifications } from "@/contexts/NotificationsContext";

import useModal from "../useModal";
import styles from "./useDestinationClipModal.module.css";

type Mode = "single" | "multiple";

/** A pickable destination (export location or transfer storage). */
export interface DestinationOption {
  id: string;
  displayName: string;
}

export interface DestinationClipModalConfig {
  /** Action verb shown in titles/buttons, e.g. "Export" or "Transfer". */
  verb: string;
  locations: DestinationOption[];
  locationsLoading: boolean;
  getLocations: () => void;
  /** Submit the action for the chosen destination + assets. */
  submit: (locationId: string, assetIds: string[]) => Promise<void>;
  destinationLabel: string;
  destinationDescription: string;
}

/**
 * Shared driver for the Clip Preview "Export" and "Transfer" flows, which are
 * identical except for the action verb, the destination source, and the
 * submit call. Both deliver an asset to an approved destination:
 *
 * - ``openSingle(clip)``: one screen (pick destination + submit).
 * - ``openMultiple(clips)``: step 1 selects clips, step 2 picks destination.
 *
 * Sub-clips have no original file to deliver, so they are not selectable in
 * the multi-clip list.
 */
const useDestinationClipModal = (config: DestinationClipModalConfig) => {
  const {
    verb,
    locations,
    locationsLoading,
    getLocations,
    submit,
    destinationLabel,
    destinationDescription,
  } = config;
  const { notifySuccess, notifyError } = useNotifications();

  const [mode, setMode] = useState<Mode>("single");
  const [step, setStep] = useState<1 | 2>(2);
  const [candidates, setCandidates] = useState<EnumeratedIconikAsset[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(
    new Set()
  );
  const [destination, setDestination] = useState<SelectProps.Option | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getLocations();
  }, [getLocations]);

  const locationOptions = useMemo<SelectProps.Option[]>(
    () => locations.map((loc) => ({ label: loc.displayName, value: loc.id })),
    [locations]
  );

  const deliverableCandidates = useMemo(
    () => candidates.filter((clip) => !clip.isSubClip),
    [candidates]
  );

  const resolvedAssetIds = useMemo(() => {
    if (mode === "single") {
      return candidates.map((clip) => clip.assetId);
    }
    return deliverableCandidates
      .filter((clip) => selectedAssetIds.has(clip.assetId))
      .map((clip) => clip.assetId);
  }, [mode, candidates, deliverableCandidates, selectedAssetIds]);

  const handleSubmit = useCallback(async () => {
    const locationId = destination?.value;
    if (!locationId || resolvedAssetIds.length === 0) {
      return;
    }
    setSubmitting(true);
    try {
      await submit(locationId, resolvedAssetIds);
      notifySuccess(
        <>
          {verb} submitted: {resolvedAssetIds.length} clip
          {resolvedAssetIds.length === 1 ? "" : "s"} sent to{" "}
          <strong>{destination?.label}</strong>. Iconik will process it in the
          background.
        </>
      );
    } catch (e) {
      console.error(e);
      notifyError(
        `${verb} Error`,
        `The ${verb.toLowerCase()} could not be submitted. Please try again later.`
      );
      // Re-throw so useModal keeps the modal open (preserving selection).
      throw e;
    } finally {
      setSubmitting(false);
    }
  }, [destination, resolvedAssetIds, submit, verb, notifySuccess, notifyError]);

  const onSelectScreen = mode === "single" || step === 2;
  const canSubmit = !!destination && resolvedAssetIds.length > 0;
  const canAdvance = resolvedAssetIds.length > 0;

  const toggleAsset = (assetId: string, checked: boolean) => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(assetId);
      } else {
        next.delete(assetId);
      }
      return next;
    });
  };

  const destinationField = (
    <FormField label={destinationLabel} description={destinationDescription}>
      <Select
        selectedOption={destination}
        onChange={({ detail }) => setDestination(detail.selectedOption)}
        options={locationOptions}
        filteringType="auto"
        empty={`No ${verb.toLowerCase()} locations available.`}
        placeholder="Choose a destination"
        {...(locationsLoading
          ? { loadingText: "Loading destinations", statusType: "loading" }
          : {})}
      />
    </FormField>
  );

  const clipSelectionScreen = (
    <SpaceBetween size="xs">
      <Box>Select the clips to {verb.toLowerCase()}.</Box>
      <div className={styles.clipList} data-testid="destination-clip-list">
        {deliverableCandidates.length === 0 ? (
          <Box color="text-status-inactive">
            None of these clips have an original file to {verb.toLowerCase()}.
          </Box>
        ) : (
          deliverableCandidates.map((clip) => (
            <label key={clip.assetId} className={styles.clipRow}>
              <Checkbox
                checked={selectedAssetIds.has(clip.assetId)}
                onChange={({ detail }) =>
                  toggleAsset(clip.assetId, detail.checked)
                }
              />
              <span className={styles.clipName} title={clip.filename}>
                {clip.filename}
              </span>
            </label>
          ))
        )}
      </div>
    </SpaceBetween>
  );

  const additionalActions =
    mode === "multiple" ? (
      step === 1 ? (
        <Button
          variant="normal"
          onClick={() => setStep(2)}
          disabled={!canAdvance}
        >
          Next
        </Button>
      ) : (
        <Button variant="normal" onClick={() => setStep(1)}>
          Back
        </Button>
      )
    ) : undefined;

  const { visible, toggle, ModalComponent } = useModal({
    title: mode === "single" ? `${verb} Clip` : `${verb} Multiple Clips`,
    onConfirm: onSelectScreen ? handleSubmit : undefined,
    confirmLabel: verb,
    loading: submitting,
    disabled: !canSubmit,
    additionalActions,
    content: onSelectScreen ? destinationField : clipSelectionScreen,
  });

  const openSingle = useCallback(
    (clip: EnumeratedIconikAsset) => {
      setMode("single");
      setStep(2);
      setCandidates([clip]);
      setSelectedAssetIds(new Set([clip.assetId]));
      setDestination(null);
      toggle();
    },
    [toggle]
  );

  const openMultiple = useCallback(
    (clips: EnumeratedIconikAsset[]) => {
      setMode("multiple");
      setStep(1);
      setCandidates(clips);
      setSelectedAssetIds(
        new Set(clips.filter((c) => !c.isSubClip).map((c) => c.assetId))
      );
      setDestination(null);
      toggle();
    },
    [toggle]
  );

  useEffect(() => {
    if (!visible) {
      setDestination(null);
      setStep(2);
      setMode("single");
      setCandidates([]);
      setSelectedAssetIds(new Set());
    }
  }, [visible]);

  return { ModalComponent, openSingle, openMultiple };
};

export default useDestinationClipModal;
